import React, { useState } from 'react';
import { Pencil } from 'lucide-react';
import { ServiceLogEntry } from '../types/analysis.types';
import CorrectServiceLogValueModal from './CorrectServiceLogValueModal';
import './ServiceLogValueCell.css';

interface ServiceLogValueCellProps {
    entry: ServiceLogEntry;
    unit?: string;
    serviceTitle: string;
    /** Admin and the service collects a value */
    canCorrect: boolean;
    getUserName: (uid: string) => string;
    onCorrected: () => void;
}

const withUnit = (value: number, unit?: string) => `${value}${unit ? ` ${unit}` : ''}`;

/** Mängd in the log modal: the value, a "korrigerad" history toggle and, for admins, a pencil. */
const ServiceLogValueCell: React.FC<ServiceLogValueCellProps> = ({
    entry, unit, serviceTitle, canCorrect, getUserName, onCorrected,
}) => {
    const [showHistory, setShowHistory] = useState(false);
    const [editing, setEditing] = useState(false);

    if (entry.value === undefined) return null;
    const corrected = entry.corrections.length > 0;

    return (
        <div className="service-log-value">
            <div className="service-log-value-row">
                <span>{withUnit(entry.value, unit)}</span>
                {corrected && (
                    <button
                        type="button"
                        className="service-log-corrected-badge"
                        aria-expanded={showHistory}
                        onClick={() => setShowHistory(!showHistory)}
                    >
                        korrigerad
                    </button>
                )}
                {canCorrect && (
                    <button
                        type="button"
                        className="service-log-edit-button"
                        aria-label="Korrigera värde"
                        title="Korrigera värde"
                        onClick={() => setEditing(true)}
                    >
                        <Pencil size={14} />
                    </button>
                )}
            </div>
            {corrected && showHistory && (
                <ul className="service-log-history">
                    {entry.corrections.map(correction => (
                        <li key={correction.key}>
                            <div>
                                {withUnit(correction.from, unit)} → <strong>{withUnit(correction.to, unit)}</strong>
                            </div>
                            <div className="service-log-history-meta">
                                {correction.changedWhen.slice(0, 16)} · {getUserName(correction.changedBy)}
                            </div>
                            {correction.reason && <div className="service-log-history-reason">{correction.reason}</div>}
                        </li>
                    ))}
                </ul>
            )}
            {editing && (
                <CorrectServiceLogValueModal
                    logRef={{
                        year: entry.year,
                        serviceType: entry.serviceType ?? '',
                        buildingKey: entry.buildingKey,
                        logId: entry.logId,
                    }}
                    serviceTitle={serviceTitle}
                    currentValue={entry.value}
                    unit={unit}
                    onHide={() => setEditing(false)}
                    onSaved={() => {
                        setEditing(false);
                        onCorrected();
                    }}
                />
            )}
        </div>
    );
};

export default ServiceLogValueCell;
